import React, { useMemo } from "react";
import { List, RowComponentProps } from "react-window";
import { useInfiniteLoader } from "react-window-infinite-loader";
import { useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";

interface IListboxComponent {
  children?: React.ReactNode;
  hasNextPage?: boolean;
  isNextPageLoading?: boolean;
  loadNextPage?: (startIndex: number, stopIndex: number) => Promise<void>;
  smallSc?: boolean;
}

const LISTBOX_PADDING = 8; // px

// Render an item or a loading indicator.
type ItemRowProps = { items: React.ReactNode[] };

const Item = (props: RowComponentProps<ItemRowProps>) => {
  const { items, index, style, ariaAttributes } = props;
  const item = items[index];

  return React.isValidElement(item)
    ? React.cloneElement(item as React.ReactElement<any>, {
        style: {
          ...style,
          // top: (style.top as number) + LISTBOX_PADDING,
        },
        ...ariaAttributes,
      })
    : null;
};

// Adapter for react-window
const ListboxComponent = React.forwardRef<HTMLDivElement>(
  function ListboxComponent(props: IListboxComponent, ref) {
    const {
      children,
      hasNextPage,
      isNextPageLoading,
      loadNextPage,
      smallSc,
      ...other
    } = props;
    const itemData = React.Children.toArray(children);
    const theme = useTheme();
    const xsScreen = useMediaQuery(theme.breakpoints.between(0, 300), {
      noSsr: true,
    });
    const smScreen = useMediaQuery(theme.breakpoints.between(301, 600), {
      noSsr: true,
    });
    const itemCount = hasNextPage ? itemData.length + 1 : itemData.length;
    const itemSize = useMemo(() => {
      if (xsScreen && smallSc) return 80;
      else {
        return xsScreen || smScreen ? 55 : 36;
      }
    }, [xsScreen, smScreen, smallSc]);

    // Only load 1 page of items at a time.
    // Pass an empty callback to InfiniteLoader in case it asks us to load more than once.
    const loadMoreRows = isNextPageLoading
      ? async () => {}
      : loadNextPage || (async () => {});

    // Every row is loaded except for our loading indicator row.
    const isItemLoaded = (index: number) => {
      return !hasNextPage || index < itemData.length;
    };

    const getHeight = () => {
      if (itemCount > 8) {
        return 8 * itemSize;
      }
      return itemData.length * itemSize;
    };

    const onRowsRendered = useInfiniteLoader({
      isRowLoaded: isItemLoaded,
      loadMoreRows,
      rowCount: itemCount,
    });

    return (
      <div ref={ref} {...other}>
        <List
          rowHeight={itemSize}
          overscanCount={5}
          rowCount={itemCount}
          onRowsRendered={onRowsRendered}
          rowComponent={Item}
          rowProps={{ items: itemData }}
          style={{ height: getHeight() + 2 * LISTBOX_PADDING, width: "100%" }}
          tagName="ul"
        />
      </div>
    );
  }
);

export default ListboxComponent;
